import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { login } from "../api/auth";
import { ApiError } from "../api/client";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { RisographBackground } from "../components/RisographBackground";
import { useAuth } from "../hooks/useAuth";

export function LoginPage() {
	const { login: setToken } = useAuth();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();
		setError(null);
		setLoading(true);
		try {
			const { access_token, name, language, onboarding_complete } = await login(
				email,
				password,
			);
			setToken(access_token, name, language, onboarding_complete);
			navigate(onboarding_complete === false ? "/onboarding/prompts" : "/home");
		} catch (err) {
			if (err instanceof ApiError && err.status === 401) {
				setError(t("auth.login.errorInvalid"));
			} else {
				setError(t("auth.login.errorGeneric"));
			}
		} finally {
			setLoading(false);
		}
	}

	return (
		<div className="min-h-screen flex items-center justify-center px-6">
			<RisographBackground />
			<div className="w-full max-w-md">
				<h1 className="font-display text-5xl font-bold text-ink mb-2 leading-tight">
					{t("auth.login.headline")}
				</h1>
				<p className="font-body italic text-ink/60 text-lg mb-10">
					{t("auth.login.subheadline")}
				</p>

				<form onSubmit={handleSubmit} className="space-y-8">
					<div className="border-b border-ink/30 pb-1">
						<input
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							placeholder={t("auth.login.emailPlaceholder")}
							required
							className="w-full bg-transparent font-body text-lg text-ink placeholder-ink/30 outline-none"
						/>
					</div>
					<div className="border-b border-ink/30 pb-1">
						<input
							type="password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							placeholder={t("auth.login.passwordPlaceholder")}
							required
							className="w-full bg-transparent font-body text-lg text-ink placeholder-ink/30 outline-none"
						/>
					</div>

					{error && (
						<p className="font-ui text-sm text-coral uppercase tracking-wide">
							{error}
						</p>
					)}

					<button
						type="submit"
						disabled={loading}
						className="ink-btn w-full py-3 font-ui text-sm uppercase tracking-widest"
					>
						{loading
							? t("auth.login.submitLoading")
							: t("auth.login.submitIdle")}
					</button>
				</form>

				<p className="font-ui text-xs uppercase tracking-widest text-ink/40 mt-8 text-center">
					{t("auth.login.noAccount")}{" "}
					<Link
						to="/register"
						className="text-ink/70 underline underline-offset-2"
					>
						{t("auth.login.beginHere")}
					</Link>
				</p>

				<div className="mt-8 flex justify-center">
					<LanguageSwitcher />
				</div>
			</div>
		</div>
	);
}
